import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  HostListener,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { CentralMailStatus, NotificationsStore } from '@store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PATTERN } from '../../../../shared/common';
import { TrimPatternValidator } from '../../../../shared/validators/trim-pattern/trim-pattern-validator';

@Component({
  selector: 'app-configure-central-email',
  templateUrl: './configure-central-email.component.html',
  styleUrls: ['./configure-central-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigureCentralEmailComponent implements OnInit, OnDestroy {
  CentrailMailStatus = CentralMailStatus;

  centralEmailForm = new FormControl<string>(null, [TrimPatternValidator(PATTERN.EMAIL)]);
  currentEmail: string;

  tileFocused = false;
  pendingChanges = false;
  blockSaving = true;
  dataSaving = false;

  @HostListener('document:click', ['$event.target'])
  public onPageClick(targetElement): void {
    const clickedOnTile = this.elementRef.nativeElement.contains(targetElement);
    if (clickedOnTile) {
      this.tileFocused = true;
    } else {
      this.tileFocused = false;
    }
  }

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(public notificationsStore: NotificationsStore, private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.notificationsStore.getCentralEmail();

    this.onCentralEmailUpdated();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onInputValueChange(value: string): void {
    this.centralEmailForm.setValue(value);
    this.pendingChanges = true;
    this.tileFocused = true;

    this.blockSaving = this.centralEmailForm.value === this.currentEmail;
  }

  onSave(): void {
    this.setNewCentralEmail();
    this.pendingChanges = false;
    this.tileFocused = true;
    this.dataSaving = true;
  }

  resetToDefault(): void {
    this.centralEmailForm.setValue('');
    this.onSave();
  }

  cancelChanges(): void {
    this.centralEmailForm.setValue(this.currentEmail);
    this.pendingChanges = false;
    this.tileFocused = false;
  }

  private setNewCentralEmail(): void {
    const isNewEmail = this.currentEmail == null && !!this.centralEmailForm.value;
    const isEmailChanged =
      !!this.currentEmail &&
      !!this.centralEmailForm.value &&
      this.centralEmailForm.value !== this.currentEmail;
    const isEmailRemoved = !!this.currentEmail && !this.centralEmailForm.value;

    if (isNewEmail) {
      this.notificationsStore.setCentralEmail(this.centralEmailForm.value);
    } else if (isEmailChanged) {
      this.notificationsStore.updateCentralEmail(this.centralEmailForm.value);
    } else if (isEmailRemoved) {
      this.notificationsStore.deleteCentralEmail();
    }
  }

  private onCentralEmailUpdated(): void {
    this.notificationsStore.centralEmail$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((email) => {
      this.currentEmail = email;
      this.centralEmailForm.setValue(email);
      this.pendingChanges = false;
      this.blockSaving = true;
      this.dataSaving = false;
      this.tileFocused = false;
    });
  }
}
